import { combineLatest, fromEvent } from "rxjs";
import { IMessagingService } from "../services";
import { IAnalyticsService } from "../services/interfaces/analyticsService";
import { AccordionSection } from "./accordionSection";
import { Component } from "./component";
import { AnalyticsEventName } from "../enums/analyticsEventName";
import { AccordionInteractionEvent } from "../models";

enum AccordionToggleAction {
    Open,
    Close
}

// VM for managing an Accordion. Contains a list of sections, and handles open/close all events 
class AccordionComponent extends Component
{
    private toggleButtonEl: HTMLButtonElement;
    private toggleButtonTextEl: HTMLElement;
    private toggleAction: AccordionToggleAction = AccordionToggleAction.Open;
    private sections: AccordionSection[] = [];
    private analyticsService: IAnalyticsService;
    private accordionLocation: string;

    constructor(componentEl: HTMLElement, messagingService: IMessagingService, analyticsService: IAnalyticsService) {
        super(componentEl, messagingService);

        // bind elements
        this.toggleButtonEl = componentEl.querySelector<HTMLButtonElement>(":scope > .js-accordion-toggle-button");
        if (this.toggleButtonEl) {
            this.toggleButtonTextEl = this.toggleButtonEl.querySelector<HTMLElement>(".js-accordion-toggle-button-text");
        }

        this.analyticsService = analyticsService;
        const sections = componentEl.querySelectorAll<HTMLElement>(":scope > .js-accordion-section");

        if (this.toggleButtonEl && sections.length === 1) {
            this.toggleButtonEl.style.display = 'none';
        }
        
        let initAllClosed;
        if (componentEl.dataset.initState) {
            initAllClosed = componentEl.dataset.initState === 'closed' ? true : false;
        }

        sections.forEach((el, index) => {
            let section;
            this.accordionLocation = el.dataset["accordionLocation"];

            if (initAllClosed !== undefined) {
                section = new AccordionSection(el, this.analyticsService,initAllClosed);
            } else {
                const initOpened = index !== 0 && sections.length !== 1;
                section = new AccordionSection(el, this.analyticsService, initOpened);
            }
            this.sections.push(section);
        });

        if (this.toggleButtonEl) {
            const click$ = fromEvent(this.toggleButtonEl, 'click');
            click$.subscribe(() => {
                this.toggleAction === AccordionToggleAction.Open ? this.openAll() : this.closeAll();
            })
        }

        // every time one of the sections are opened check to see if they all are. If so, 
        // update the close/open all button
        const onSectionChange = combineLatest(this.sections.map(s => s != null && s.onStateChange));
        
        onSectionChange.subscribe((sectionStates) => {
            if (sectionStates.every(value => value)) {
                this.setToggleState(AccordionToggleAction.Close);
            }
            else if (sectionStates.every(value => !value)) {
                this.setToggleState(AccordionToggleAction.Open);
            }
        });
    }

    public openAll() {
        this.sections.forEach(section => { section.open(); });
        this.setToggleState(AccordionToggleAction.Close);
        const analyticsData = new AccordionInteractionEvent(
            "All",
            0,
            "accordion all sections shown",
            this.accordionLocation
        );

        this.analyticsService.RecordEvent(AnalyticsEventName.AccordionInteraction, analyticsData);

    }

    public closeAll() {
        this.sections.forEach(section => { section.close(); });
        this.setToggleState(AccordionToggleAction.Open);
        const analyticsData = new AccordionInteractionEvent(
            "All",
            0,
            "accordion all sections hidden",
            this.accordionLocation
        );

        this.analyticsService.RecordEvent(AnalyticsEventName.AccordionInteraction, analyticsData);
    }

    private setToggleState(state) {
        if (this.toggleButtonTextEl) {
            this.toggleButtonTextEl.innerText = state === AccordionToggleAction.Close ? "Close all" : "Show all";
        }
        this.toggleAction = state;
    }
};

export { AccordionComponent };
